import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCapabilities } from "@/context/CapabilitiesContext";
import { Rocket, X, ArrowRight, Warning } from "@phosphor-icons/react";
import Onboarding from "@/pages/Onboarding";

// Persistent banner + one-time modal on first render (per session) for incomplete onboarding.
// Also shows offboarding status banner if user is being offboarded.
const MODAL_KEY = "velacin_ob_modal_dismissed_session";

export default function OnboardingGate() {
  const { user } = useAuth();
  const { can } = useCapabilities();
  const location = useLocation();
  const [ob, setOb] = useState(null);
  const [off, setOff] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [bannerHidden, setBannerHidden] = useState(false);

  useEffect(() => {
    if (!user || !can("use_onboarding")) return;
    (async () => {
      try {
        const [obR, offR] = await Promise.all([
          api.get("/onboarding/me").catch(() => null),
          api.get("/offboarding/me").catch(() => null),
        ]);
        setOb(obR?.data || null);
        setOff(offR?.data || null);
        // Auto-open modal on first render of a session if not complete
        const dismissed = sessionStorage.getItem(MODAL_KEY);
        if (obR?.data?.status !== "complete" && !dismissed && !location.pathname.startsWith("/onboarding")) {
          setModalOpen(true);
        }
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const closeModal = () => {
    setModalOpen(false);
    sessionStorage.setItem(MODAL_KEY, "1");
  };

  if (!user || !can("use_onboarding")) return null;

  const incomplete = ob && ob.status !== "complete";
  const pct = ob?.progress?.required_percent ?? 0;
  const doneReq = ob?.progress?.completed_required ?? 0;
  const totalReq = ob?.progress?.total_required ?? 0;

  const isOnOnboarding = location.pathname.startsWith("/onboarding");

  return (
    <>
      {/* Banner — hidden on the onboarding page itself */}
      {incomplete && !isOnOnboarding && !bannerHidden && (
        <div className="bg-[#002FA7] text-white px-6 py-2.5 text-sm flex items-center justify-between gap-3" data-testid="onboarding-banner">
          <div className="flex items-center gap-2 min-w-0">
            <Rocket size={14} weight="bold" className="shrink-0" />
            <span className="truncate">
              <strong>Complete your onboarding</strong> — {doneReq}/{totalReq} required items done ({pct}%)
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Link to="/onboarding" className="bg-white text-[#002FA7] px-3 py-1 text-xs font-semibold hover:opacity-90 flex items-center gap-1" data-testid="onboarding-banner-cta">
              Continue <ArrowRight size={11} weight="bold" />
            </Link>
            <button onClick={() => setBannerHidden(true)} className="hover:opacity-70" title="Hide for now">
              <X size={14} weight="bold" />
            </button>
          </div>
        </div>
      )}

      {/* Offboarding banner (if a record exists and is in progress) */}
      {off && off.status !== "complete" && !location.pathname.startsWith("/offboarding") && (
        <div className="bg-[#F59E0B] text-white px-6 py-2.5 text-sm flex items-center justify-between gap-3" data-testid="offboarding-banner">
          <div className="flex items-center gap-2 min-w-0">
            <Warning size={14} weight="bold" className="shrink-0" />
            <span className="truncate">
              Your offboarding is in progress — last working day <strong>{off.last_working_day}</strong>. Please complete your handover checklist.
            </span>
          </div>
          <Link to="/offboarding" className="bg-white text-[#F59E0B] px-3 py-1 text-xs font-semibold hover:opacity-90 flex items-center gap-1" data-testid="offboarding-banner-cta">
            Open <ArrowRight size={11} weight="bold" />
          </Link>
        </div>
      )}

      {/* Auto-open modal (dismissible) */}
      {modalOpen && incomplete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeModal}>
          <div onClick={e => e.stopPropagation()} className="w-full max-w-5xl">
            <Onboarding modal onClose={closeModal} />
          </div>
        </div>
      )}
    </>
  );
}
